<script setup lang="ts">
import { tpgIconMap } from '@tpg/grammar-web-icons';
import { computed } from 'vue';

import {
  GrammarDivider,
  GrammarLocalLoader,
} from '@nora-monorepo/grammar-ui';

import DrawLayersGroup from '#nora-ui/map/components/overlay-menu/groups/DrawLayersGroup.vue';
import SimpleLayerGroup from '#nora-ui/map/components/overlay-menu/groups/SimpleLayerGroup.vue';
import ToggleAllLayersVisibility from '#nora-ui/map/components/overlay-menu/groups/ToggleAllLayersVisibility.vue';
import ToggleCluster from '#nora-ui/map/components/overlay-menu/groups/ToggleCluster.vue';
import TracksGroup from '#nora-ui/map/components/overlay-menu/groups/TracksGroup.vue';
import FileLayerMenuItem from '#nora-ui/map/components/overlay-menu/items/FileLayerMenuItem.vue';
import NotificationLayerMenuItem from '#nora-ui/map/components/overlay-menu/items/NotificationLayerMenuItem.vue';
import OverlayMenuFooter from '#nora-ui/map/components/overlay-menu/OverlayMenuFooter.vue';
import MenuGroup from '#nora-ui/map/components/overlay-menu/primitives/MenuGroup.vue';
import { useMapStore } from '#nora-ui/map/stores/map-store';
import { useOperationStore } from '#nora-ui/stores/operation-store';

const props = defineProps<{
  mapId: string;
  operationId: string;
}>();

const { mapId } = props;

const mapStore = useMapStore();
const operationStore = useOperationStore();
const map = mapStore.getMapByIdOrThrow(mapId);

const operationExists = computed(() => operationStore.getOperation(props.operationId) !== undefined);

const baseMaps = computed(() =>
  map.layers.getAvailableBaseMaps().map(baseMap => ({
    id: baseMap.id,
    name: baseMap.name ?? baseMap.id,
    visible: map.ref.baseMap === baseMap.id,
    toggleVisibility: () => {
      map.setBaseMap(baseMap.id);
    },
  })),
);

const mapOverlays = computed(
  () =>
    map.ref.overlayMaps.map(overlayMap => ({
      id: overlayMap.id,
      name: overlayMap.name ?? overlayMap.id,
      visible: overlayMap.visible ?? false,
      toggleVisibility: () => {
        map.toggleOverlayMapVisibility(overlayMap.id);
      },
    })),
);

const offlineMaps = computed(
  () =>
    map.ref.offlineMaps.map(offlineMap => ({
      id: offlineMap.id,
      name: offlineMap.name ?? offlineMap.id,
      visible: offlineMap.visible,
      toggleVisibility: () => {
        map.toggleOfflineMapVisibility(offlineMap.id);
      },
    })),
);
</script>

<template>
  <div class="h-full flex flex-col">
    <GrammarLocalLoader v-if="map.ref.isLoading" />

    <template v-else>
      <ToggleAllLayersVisibility :map-id="mapId" />
      <ToggleCluster :map-id="mapId" />
      <GrammarDivider orientation="horizontal" />

      <div class="flex flex-1 flex-col gap-5">
        <template v-if="operationExists">
          <DrawLayersGroup
            :map-id="mapId"
            :operation-id="props.operationId"
          />
          <TracksGroup
            :map-id="mapId"
            :operation-id="props.operationId"
          />
        </template>

        <MenuGroup title="Other">
          <FileLayerMenuItem :map-id="mapId" />

          <GrammarDivider
            emphasis="low-emphasis"
            class="px-2"
          />

          <NotificationLayerMenuItem :map-id="mapId" />
        </MenuGroup>

        <SimpleLayerGroup
          v-if="offlineMaps.length"
          title="Offline maps"
          :items="offlineMaps"
        />
        <SimpleLayerGroup
          title="Map overlays"
          :items="mapOverlays"
          :icon="tpgIconMap"
        />
        <SimpleLayerGroup
          title="Base maps"
          :items="baseMaps"
          :icon="tpgIconMap"
          :is-radio-button="true"
          item-class="pb-1"
        />
      </div>
      <div class="sticky -bottom-2">
        <OverlayMenuFooter :map-id="mapId" />
      </div>
    </template>
  </div>
</template>
